import React from 'react';
import { useTranslation } from 'react-i18next';
import { createPlaceholderUrl } from '../../../../../utils/PlaceholderCreator';

const SkillsPanelMobileCard = ({
    item,
    skill,
    calculatedCost,
    adjustedScore,
    originalScore,
    multiplier,
    aptitude,
    rank,
    isCalculated,
    isUpgradeActive,
    efficiencyRank,
    showRank,
    shouldHighlight,
    onToggleSkill,
    getIconUrl,
    onToggleFastLearner,
    onLevelChange,
    onToggleIgnoreCost,
    onUpgradeSkill,
    onRemoveSkill,
    staggerDelay
}) => {
    const { t } = useTranslation();

    // DEBUG: Verify Render Count
    console.log(`[Render] MobileCard - Skill ID: ${skill.Id}`);

    return (
        <tr
            className={`
                block md:hidden border-b border-gray-100 last:border-0
                animate-fade-in-up
            `}
            style={{
                animationDelay: staggerDelay
            }}
        >
            <td className="block p-2">
                <div className="bg-gray-50 rounded-xl p-3 relative shadow-sm">

                    {/* CONTENT WRAPPER - Applies Grayscale/Opacity/Scale when inactive */}
                    <div className={`transition-transform duration-200 ${!shouldHighlight ? 'opacity-60 grayscale scale-95' : 'scale-100'}`}>

                        {/* Top Row: Icon (Toggle), Name, Rank */}
                        <div className="flex items-start gap-3 mb-2">
                            {/* Icon - CLICK TO TOGGLE */}
                            <div
                                onClick={() => onToggleSkill(skill.Id)}
                                className="w-12 h-12 bg-white rounded-lg flex-shrink-0 overflow-hidden border border-gray-200 shadow-sm relative cursor-pointer active:scale-95"
                            >
                                <img
                                    src={getIconUrl(skill.icon_id)}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                    decoding="async"
                                    onError={(e) => { e.target.src = createPlaceholderUrl(48, 48, '?'); }}
                                />

                                {/* Active Indicator Overlay */}
                                {item.active && (
                                    <div className="absolute inset-0 bg-green-500/20 ring-2 ring-inset ring-green-500 rounded-lg"></div>
                                )}

                                {showRank && (
                                    <div className={`absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shadow-sm border z-10 ${efficiencyRank <= 5 ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                        {efficiencyRank}
                                    </div>
                                )}
                            </div>

                            {/* Name & Info */}
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-gray-800 text-sm leading-tight mb-0.5">{skill.Skill_name}</div>
                                {/* Compact Info Line: Orig | Apt | Mult */}
                                <div className="text-[10px] text-gray-500 flex flex-wrap gap-x-2 leading-tight">
                                    <span>{t('SkillsRow.label_orig')} <span className="font-medium text-gray-700">{originalScore || '-'}</span></span>
                                    {isCalculated && (
                                        <>
                                            <span className="text-gray-300 mx-1">|</span>
                                            <span>{aptitude} <span className="text-gray-400">({rank})</span>: <span className="text-blue-600 font-medium">{multiplier}%</span></span>
                                        </>
                                    )}
                                </div>
                                <div className="text-[10px] text-gray-400 mt-0.5 truncate">{skill.Description}</div>
                            </div>
                        </div>

                        {/* Stats Row: Level (Compact Dropdown) & Cost/Score */}
                        <div className="flex items-center gap-2 mb-2">
                            {/* Level Selector - COMPACT DROPDOWN [ 1 ] */}
                            <div className="relative w-12 h-8 bg-white border border-gray-200 rounded-lg shadow-sm flex items-center justify-center">
                                <span className="font-bold text-gray-700 text-sm">
                                    {item.level === 5 ? 'M' : item.level}
                                </span>
                                <select
                                    value={item.level}
                                    onChange={(e) => onLevelChange(skill.Id, parseInt(e.target.value))}
                                    className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
                                >
                                    {[0, 1, 2, 3, 4, 5].map(lvl => (
                                        <option key={lvl} value={lvl}>{lvl === 5 ? t('SkillsRow.label_max') : lvl}</option>
                                    ))}
                                </select>
                                {/* Tiny chevron indicator */}
                                <div className="absolute bottom-0.5 right-1 pointer-events-none">
                                    <svg className="w-2 h-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7"></path></svg>
                                </div>
                            </div>

                            {/* Combined Cost/Score Block */}
                            <div className="flex flex-1 bg-white border border-gray-200 rounded-lg shadow-sm h-8 overflow-hidden">
                                {/* Cost */}
                                <div
                                    className={`flex-1 px-1 flex flex-col justify-center items-center border-r border-gray-100 cursor-pointer ${item.ignoreCost ? 'bg-red-50' : ''}`}
                                    onClick={() => onToggleIgnoreCost && onToggleIgnoreCost(skill.Id)}
                                >
                                    <span className="text-[8px] text-gray-400 uppercase leading-none mb-[1px]">{t('SkillsPanel.header_cost')}</span>
                                    <span className={`text-[10px] font-mono font-bold leading-none ${item.ignoreCost ? 'line-through text-red-500' : 'text-gray-700'}`}>{calculatedCost}</span>
                                </div>
                                {/* Score */}
                                <div className="flex-1 px-1 flex flex-col justify-center items-center">
                                    <span className="text-[8px] text-gray-400 uppercase leading-none mb-[1px]">{t('SkillsPanel.header_score')}</span>
                                    <span className="text-[10px] font-bold text-green-600 leading-none">{isCalculated ? adjustedScore : (originalScore || '-')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Progress Bar (if calculated) */}
                        {isCalculated && (
                            <div className="mb-2">
                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"
                                        style={{ width: `${Math.min((adjustedScore / 50000) * 100, 100)}%` }}
                                    ></div>
                                </div>
                                <div className="flex justify-between mt-1 text-[9px] text-gray-400">
                                    <span>{t('SkillsRow.label_efficiency')}: {item.efficiency ? item.efficiency.toFixed(2) : '-'}</span>
                                </div>
                            </div>
                        )}

                    </div>
                    {/* END CONTENT WRAPPER */}

                    {/* ACTIONS ROW - Outside Grayscale Wrapper */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200/50 mt-1">
                        {/* Left: Fast Learner Toggle */}
                        <button
                            onClick={() => onToggleFastLearner && onToggleFastLearner(skill.Id)}
                            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full border shadow-sm transition-all ${item.fastLearner ? 'text-white transform scale-105' : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50'}`}
                            style={item.fastLearner ? { backgroundColor: 'rgb(255, 151, 64)', borderColor: 'rgb(226, 105, 1)' } : {}}
                        >
                            {t('SkillsRow.label_fast_learner')}
                        </button>

                        {/* Right Actions: Upgrade & Remove */}
                        <div className="flex items-center gap-2">
                            {skill.upgrade_id && !isUpgradeActive && (
                                <button onClick={() => onUpgradeSkill && onUpgradeSkill(skill.Id)} className="p-1.5 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 shadow-sm border border-blue-100" title={t('SkillsRow.tooltip_upgrade')}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                                    </svg>
                                </button>
                            )}
                            <button onClick={() => onRemoveSkill(skill.Id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all" title={t('SkillsRow.tooltip_remove')}>
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        </div>
                    </div>

                </div>
            </td>
        </tr>
    );
};

export default React.memo(SkillsPanelMobileCard);
// Revisado: 2025-01-07
